// lib/builderFeedChannel.ts — H4a (COCKPIT-HOME-COLUMNS-SPEC.md §H4): the
// project screen's builder feed, OUTBOUND ONLY. Implements the SAME
// `AtlasChannel` seam atlasChannel.ts already defines, so CockpitFrame /
// AtlasColumn render it without knowing the difference — `connected` turns
// true on the first successful read; `items` is the feed, NEWEST FIRST
// (the server's own order, GET /api/cockpit/builder/:project/items),
// unlike atlasChannel's own oldest-first conversation. `send`/`setDraft`
// are deliberate no-ops: there is nowhere to send a message TO a builder
// yet (H4b's own gate) — AtlasColumn.tsx disables the composer outright
// for a builder occupant rather than relying on `connected`.
//
// Polls every 30s while the tab is visible (the brief's own number);
// stops while hidden, catches up once visible again — same discipline
// CoreAtlasChannel's own long-poll loop already keeps.
import type { AtlasChannel, AtlasChannelItem } from './atlasChannel';
import { getBuilderFeedItems, type BuilderFeedItemApi } from './api';

const DEFAULT_POLL_MS = 30_000;

/** The slice of `document` the channel watches — same shape
 * atlasChannel.ts's own `VisibilitySource` names. */
export interface BuilderFeedVisibilitySource {
  readonly visibilityState: string;
  addEventListener(type: 'visibilitychange', listener: () => void): void;
  removeEventListener(type: 'visibilitychange', listener: () => void): void;
}

export interface BuilderFeedChannelOptions {
  tenantId: string;
  project: string;
  /** Injected by tests; the real fetch-backed api.ts call otherwise. */
  fetchItems?: typeof getBuilderFeedItems;
  /** Injected by tests; the page's document otherwise. */
  doc?: BuilderFeedVisibilitySource;
  pollMs?: number;
}

function toChannelItem(row: BuilderFeedItemApi): AtlasChannelItem | null {
  switch (row.kind) {
    case 'status':
      return { kind: 'status', id: row.id, text: row.text, at: row.at };
    case 'action':
    case 'task_opened':
    case 'task_closed':
      return { kind: row.kind, id: row.id, text: row.text, at: row.at };
    default:
      // A kind this file does not know about (added server-side before
      // this chunk names it) renders as nothing — never a throw, the same
      // defensive posture atlasChannel.ts's own header documents.
      return null;
  }
}

class BuilderFeedChannel implements AtlasChannel {
  connected = false;
  items: AtlasChannelItem[] = [];
  /** Never actually sent — see this file's header. */
  draft = '';

  private readonly listeners = new Set<() => void>();
  private readonly fetchItems: typeof getBuilderFeedItems;
  private readonly doc: BuilderFeedVisibilitySource | null;
  private readonly pollMs: number;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private disposed = false;
  private loading = false;

  constructor(private readonly opts: BuilderFeedChannelOptions) {
    this.fetchItems = opts.fetchItems ?? getBuilderFeedItems;
    this.doc = opts.doc ?? (typeof document !== 'undefined' ? document : null);
    this.pollMs = opts.pollMs ?? DEFAULT_POLL_MS;
    this.doc?.addEventListener('visibilitychange', this.onVisibility);
    void this.load();
  }

  setDraft(_text: string): void {
    // Nowhere to send — see this file's header. Never a throw: a stray
    // call (there shouldn't be one, since the composer is disabled) stays
    // inert, same posture DisconnectedAtlasChannel's own send takes.
  }

  send(_text: string): void {
    // Outbound only this chunk — see this file's header.
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  dispose(): void {
    this.disposed = true;
    if (this.timer) clearTimeout(this.timer);
    this.doc?.removeEventListener('visibilitychange', this.onVisibility);
  }

  private readonly onVisibility = (): void => {
    if (this.hidden()) {
      if (this.timer) {
        clearTimeout(this.timer);
        this.timer = null;
      }
    } else {
      void this.load();
    }
  };

  private hidden(): boolean {
    return this.doc?.visibilityState === 'hidden';
  }

  private scheduleNext(): void {
    if (this.disposed || this.hidden()) return;
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => void this.load(), this.pollMs);
  }

  private async load(): Promise<void> {
    if (this.disposed || this.hidden() || this.loading) return;
    this.loading = true;
    try {
      const { items } = await this.fetchItems(this.opts.tenantId, this.opts.project);
      this.connected = true;
      this.items = items.map(toChannelItem).filter((i): i is AtlasChannelItem => i !== null);
      this.notify();
    } catch {
      // Never a throw out of this async method — it runs unawaited
      // (`void this.load()`, from the constructor, a visibility change,
      // and the poll timer alike), so anything thrown here becomes an
      // UNHANDLED REJECTION, not a caught error (found in Tier 2:
      // CockpitTopbarFill.test.tsx mounts a project screen with no mock
      // for this fetch at all, a real ECONNREFUSED in happy-dom). There
      // is no composer to carry a "not sent" sentence here either way —
      // the column's own disconnected copy (AtlasColumn.tsx) already
      // covers a read that never succeeded; `connected` simply stays
      // whatever it last was.
    } finally {
      this.loading = false;
      this.scheduleNext();
    }
  }

  private notify(): void {
    for (const l of this.listeners) l();
  }
}

export function createBuilderFeedChannel(opts: BuilderFeedChannelOptions): AtlasChannel {
  return new BuilderFeedChannel(opts);
}
